'use client';

import { type PropsWithChildren, useEffect, useRef } from 'react';

import { createSafeContext } from '../../react/createSafeContext';
import type { PersonalizationClient } from '../core/PersonalizationClient';

const [PersonalizationContextProvider, usePersonalizationContext] =
  createSafeContext<PersonalizationClient>('PersonalizationContext');

export { usePersonalizationContext };

/**
 * React hook to access the PersonalizationClient from context.
 * Must be used within a PersonalizationProvider.
 *
 * @returns {PersonalizationClient} The personalization client instance
 * @throws {Error} If used outside PersonalizationProvider
 *
 * @example
 * function ProductCard({ productId }) {
 *   const tracker = usePersonalizationTracker();
 *
 *   const handleClick = () => {
 *     tracker.click(productId, 'product_card', { position: 1 });
 *   };
 *
 *   return <div onClick={handleClick}>...</div>;
 * }
 *
 * @example
 * // Track view on mount
 * function ProductDetail({ productId }) {
 *   const tracker = usePersonalizationTracker();
 *
 *   useEffect(() => {
 *     tracker.view(productId, 'product_detail');
 *   }, [tracker, productId]);
 *
 *   return <div>...</div>;
 * }
 */
export function usePersonalizationTracker() {
  return usePersonalizationContext();
}

/**
 * Props for PersonalizationProvider.
 *
 * @property {PersonalizationClient} client - Configured personalization client instance
 */
interface PersonalizationProviderProps {
  client: PersonalizationClient;
  autoInitialize?: boolean;
}

/**
 * React context provider for PersonalizationClient.
 * Handles client initialization.
 * Must wrap any components that use personalization tracking.
 *
 * @param {PropsWithChildren<PersonalizationProviderProps>} props - Provider props
 *
 * @example
 * // App setup
 * const client = new PersonalizationClient({
 *   resolveActor: (currentActorId) => api.resolveActor(currentActorId),
 *   sendEvent: (payload) => api.trackEvent(payload),
 *   storage: new WebPersonalizationStorageAdapter()
 * });
 *
 * function App() {
 *   return (
 *     <PersonalizationProvider client={client}>
 *       <YourApp />
 *     </PersonalizationProvider>
 *   );
 * }
 */
export function PersonalizationProvider({
  client,
  children,
  autoInitialize = true,
}: PropsWithChildren<PersonalizationProviderProps>) {
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!autoInitialize || isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    client.initialize();
  }, [client, autoInitialize]);

  return <PersonalizationContextProvider value={client}>{children}</PersonalizationContextProvider>;
}
